/**
 * 请求唯一出口（全项目只有这里 import axios / 创建实例）
 *
 * 原来有两套封装各自 axios.create 并把拦截器挂在「全局 axios」上：
 *   - src/lib/axios.js   （主接口，信封 { header, body }）
 *   - src/lib/opAxios.js （op 接口，参数平铺）
 * 结果是每发一个请求两套拦截器都会执行，域名写死、加域名要抄一份封装。
 *
 * 现在：
 *   - 只有一个 axios 实例（http），baseURL 按「服务」在每次请求时传入（见 src/config/api.js）；
 *   - 拦截器只注册一次，挂在实例上，不再污染全局 axios；
 *   - 请求体组装（信封 / 平铺 / 换 token / 高德）是纯函数，便于单独验证。
 *
 * 调用方一般不直接用它，而是用 createRequest 生成的服务对象
 * （src/lib/request/createRequest.js），或直接用它做特殊请求（如平铺 GET）。
 */
import axios from 'axios';
import store from '@/store';
import conf from '@/config';
import { getServiceBaseURL } from '@/config/api';
import localStorage from '@/utils/storage/localStorage';
import sessionStorage from '@/utils/storage/sessionStorage';
import { jump, injectCustomId } from '@/common';

/** 高德请求（同源 /amap 代理或历史绝对地址） */
// 注意：这里不能带 g 标志。模块级正则配合 test() 会保留 lastIndex，
// 同一个地址连续调用会在 true / false 之间交替 —— 一旦被判成「非高德」，
// 高德请求就会被加上 Authorization 头，跨域时浏览器要先发 OPTIONS 预检，
// 而高德不返回预检的 CORS 头 → 请求直接失败（表现就是「高德报错」）
const AMAP_RE = /restapi\.amap\.com|\/amap\//i;
/** 写请求（参数放 data，其余放 params） */
const WRITE_METHODS = ['post', 'put', 'patch'];

/** 请求体组装形态：header-body = 主接口信封；flat = 参数平铺（op 接口 / 平铺 GET） */
export const ENVELOPE = {
  HEADER_BODY: 'header-body',
  FLAT: 'flat'
};

export const isAmapUrl = url => AMAP_RE.test(url || '');

// ==================== 无效值不发送（全局统一处理） ====================
/**
 * 值是否无效（无效则该字段不发给后端）。按类型判断：
 *   - undefined / null                        → 无效
 *   - number：NaN / Infinity                   → 无效（0 是有效值）
 *   - string：空串 / 纯空白 / 'undefined'、
 *     'null'、'NaN' 这类哨兵字符串              → 无效（'0'、'false' 是有效值）
 *   - boolean：false                          → 有效（不能当空值丢掉）
 *   - array：[]                               → 无效（有元素就有效，不递归元素）
 *   - object：{}                              → 无效（有键就有效，不递归内部）
 *   - 其它（Date / File / Blob 等）            → 有效
 * @param {*} value
 * @returns {boolean}
 */
export function isInvalidValue(value) {
  if (value === undefined || value === null) return true;
  const type = typeof value;
  if (type === 'number') return !isFinite(value);
  if (type === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    return /^(undefined|null|nan)$/i.test(trimmed);
  }
  if (type === 'boolean') return false;
  if (type === 'function') return false;
  if (Array.isArray(value)) return value.length === 0;
  if (type === 'object') {
    // 只把「纯对象」的空对象当无效：Date / File / Blob 等实例自身没有可枚举键，
    // 但它们是有意义的值，不能丢
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return false;
    return Object.keys(value).length === 0;
  }
  return false;
}

/**
 * 清掉无效字段（只处理一层，不递归进嵌套结构）：返回新对象
 * @param {object} params
 * @returns {object}
 */
export function cleanInvalidFields(params) {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return params;
  }
  const result = {};
  Object.keys(params).forEach(key => {
    if (!isInvalidValue(params[key])) {
      result[key] = params[key];
    }
  });
  return result;
}

/**
 * 按请求形态清洗请求体：信封只清洗 header / body 内部，
 * 不会因为"空对象算无效"就把整个 header/body 容器删掉
 * @param {string} envelope ENVELOPE
 * @param {object} data buildRequestData 的结果
 * @returns {object}
 */
export function cleanRequestData(envelope, data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  const isEnvelope =
    envelope === ENVELOPE.HEADER_BODY && (data.header || data.body);
  if (!isEnvelope) return cleanInvalidFields(data);
  return {
    ...data,
    ...(data.header ? { header: cleanInvalidFields(data.header) } : {}),
    ...(data.body ? { body: cleanInvalidFields(data.body) } : {})
  };
}

/**
 * 本次请求是否要校验值有效性（默认要）：
 *   1）调用方显式传了 validate → 以它为准（可精确控制某个接口）
 *   2）否则看 config/index.js 的 skipValueValidateUrls（按地址片段匹配，可整类关掉）
 *   3）都没有 → 校验
 * @param {object} options { validate, url }
 * @returns {boolean}
 */
export function shouldValidateValues({ validate, url } = {}) {
  if (validate === false) return false;
  if (validate === true) return true;
  const skipList = conf.skipValueValidateUrls || [];
  return !skipList.some(item => item && (url || '').indexOf(item) !== -1);
}

/** 是否命中全局 loading 白名单 */
export function shouldShowLoading(url = '') {
  return conf.whiteListForLoading.some(item => url.includes(item));
}

function setLoading(isLoading) {
  store.commit('common/updateLoadingStatus', { isLoading });
}

function getSessionInfo() {
  return localStorage.get('session_info') || {};
}

/**
 * city_id：参数 > session 城市 > 全局城市（-1 视为没有）
 *
 * 注意：这里**不要**再兜底 localStorage 的 lastLocationCityInfo。
 * 曾按 mall_h5 main.js 的 setLocationCityId 思路补过这层兜底，结果首屏那一批请求
 * 从「不带 city_id」变成「带真实 city_id」，后端按城市过滤后 banner / 分类 / 商品都变少，
 * /mall 首屏直接塌掉（问题出现在这次改动之后）。
 * 也就是说这些接口缺 city_id 时给的是「全局数据」，贸然补全反而把结果收窄了。
 * 真要按城市取数，得让页面等城市就绪后再请求（watch currentCity 重取），而不是只补参数。
 */
function getCityId(params = {}) {
  if (params.city_id) return params.city_id;
  const cityInfo = sessionStorage.get('current_city') || {};
  if (cityInfo.id) return cityInfo.id;
  return store.state.common.currentCity.id !== -1
    ? store.state.common.currentCity.id
    : null;
}

/** channel_id：参数 > session */
function getChannelId(params = {}) {
  return (
    (!params.channel_id || params.channel_id === 'undefined'
      ? sessionStorage.get('channel_id')
      : params.channel_id) || ''
  );
}

/**
 * 组装请求参数（纯函数，便于单独验证）
 * @param {string} envelope ENVELOPE.HEADER_BODY | ENVELOPE.FLAT
 * @param {string} url 请求地址（判断高德）
 * @param {object} params 业务参数（hideAddCommonParam / hideToken / changeToken 等为本地开关，不发给后端）
 * @returns {object}
 */
export function buildRequestData(envelope, url, params = {}) {
  // 高德：原样传参，不加信封也不注入公共参数。
  // （项目里高德统一走 service/common.js 的 requestWithGet + 绝对地址，这里统一成
  //  「只要命中高德就不做任何注入」，避免出现"有的注入了 token、有的没注入"的分叉）
  if (isAmapUrl(url)) return params;

  // 换 token（核销等场景）：两种形态都走 cancel_token
  if (params.changeToken) {
    const sessionHx = localStorage.get('session_hx_info') || {};
    const rest = { ...params };
    delete rest.changeToken;
    delete rest.login;
    const cancel = {
      cancel_token: params.cancel_token || sessionHx.access_token,
      platform: params.platform || 2
    };
    return envelope === ENVELOPE.HEADER_BODY
      ? { header: cancel, body: { ...rest } }
      : { ...rest, ...cancel };
  }

  // 主接口：信封 { header: { token, platform }, body: { ...业务参数, 公共参数 } }
  if (envelope === ENVELOPE.HEADER_BODY) {
    const body = {
      ...params,
      city_id: getCityId(params),
      channel_id: getChannelId(params)
    };
    // custom_id：为空（调用方传空串、或当前没有定制 id）时整个字段都不发
    injectCustomId(body, params);
    return {
      header: {
        token: getSessionInfo().token,
        platform: 2
      },
      body
    };
  }

  // 平铺（op 接口 / 平铺 GET）：默认注入 token / platform / city_id / channel_id / custom_id
  const data = { ...params };
  // 下面两个只是本地开关（控制注入行为），不能发给后端
  delete data.hideToken;
  delete data.hideAddCommonParam;
  if (!params.hideToken) {
    data.token = params.token || getSessionInfo().token;
  }
  if (!params.hideAddCommonParam) {
    data.platform = params.platform || 2;
    data.city_id = getCityId(params);
    data.channel_id = getChannelId(params);
    // custom_id：为空（调用方传空串、或当前没有定制 id）时整个字段都不发
    injectCustomId(data, params);
  }
  return data;
}

/**
 * 统一取响应体
 * 成功给 response.data；失败给 { status, message }（保持原有返回形态，调用方据此判断）
 */
export function checkStatus(response = {}) {
  if (response.status >= 200 && response.status < 400) {
    return response.data;
  }
  const message =
    (response.data && response.data.message) || response.message || '网络异常';
  return { status: response.code, message };
}

/**
 * 组装请求头（纯函数）
 * 说明：不放在 axios 拦截器里做，是因为 axios 0.19 会把不在其白名单里的自定义
 * config 字段（如 meta）丢掉，拦截器拿不到「本次请求属于哪个服务/哪种认证」，
 * 干脆在出口处按参数直接算好再传给 axios。
 * @param {object} options { method, url, auth, token }
 * @returns {object}
 */
export function buildRequestHeaders(options = {}) {
  const { method = 'post', url, auth = 'token', token } = options;
  const headers = {};
  // 高德：不加任何自定义头 —— Content-Type / Authorization 都属于「非简单头」，
  // 会让浏览器先发 OPTIONS 预检，而高德不返回预检的 CORS 头 → 直接跨域失败
  if (isAmapUrl(url)) return headers;
  // 只有写请求带 Content-Type：GET 加了会给跨域请求平白多一次预检
  if (WRITE_METHODS.includes(method.toLowerCase())) {
    headers['Content-Type'] = 'application/json; charset=UTF-8';
  }
  // bearer：把 token 放请求头（op 接口）；其余情况 token 在信封/参数里
  if (auth === 'bearer') {
    const finalToken = token || getSessionInfo().token;
    if (finalToken) {
      headers.Authorization = `Bearer ${finalToken}`;
    }
  }
  return headers;
}

// ==================== 唯一的 axios 实例 ====================
const http = axios.create({
  timeout: 5000 // 请求超时时间
});

http.defaults.headers.post['Content-Type'] = 'application/json; charset=UTF-8';

http.interceptors.request.use(
  config => {
    // 请求头在 httpRequest 里按参数算好传入，这里只负责全局 loading
    if (shouldShowLoading(config.url)) {
      setLoading(true);
    }
    return config;
  },
  error => {
    setLoading(false);
    return Promise.reject(error);
  }
);

http.interceptors.response.use(
  response => {
    if (shouldShowLoading(response.config && response.config.url)) {
      setLoading(false);
    }
    return response;
  },
  error => {
    setLoading(false);
    // 保持原有行为：错误也 resolve，把 response 交给 checkStatus 统一判断
    return Promise.resolve(error.response);
  }
);

/**
 * 发起请求（唯一出口）
 * @param {object} options
 *  - service  服务名（见 src/config/api.js），默认 main
 *  - method   get / post / put / delete
 *  - url      接口地址（相对路径）
 *  - params   业务参数
 *  - envelope 请求体形态，默认 flat
 *  - auth     'bearer' 时带 Authorization 头；默认 token 放进信封/参数
 *  - login    是否需要登录态（无 session 时跳登录），默认 true
 *  - timeout  自定义超时（毫秒）
 *  - validate 是否按类型过滤无效值（默认 true；false = 本次原样发送，
 *             也可在 config/index.js 的 skipValueValidateUrls 里按地址关掉）
 * @returns {Promise} axios 原始响应（信封/平铺的解包由调用方或 handler 处理）
 */
export function httpRequest(options = {}) {
  const {
    service = 'main',
    method = 'post',
    url,
    params = {},
    envelope = ENVELOPE.FLAT,
    auth = 'token',
    timeout,
    login = true,
    validate
  } = options;

  if (!url) {
    return Promise.reject(new Error('[request] 缺少请求地址 url'));
  }

  const sessionInfo = localStorage.get('session_info') || {};
  // 登录校验：与原来 lib/axios.js 完全一致（session 取不到时给的是 {}，
  // 所以这里实际不会触发跳转；保持原样，避免突然把公开页面的请求拦到登录页）
  if (login && !sessionInfo) {
    jump('/login?redirect_url=' + encodeURIComponent(window.location.href));
    return Promise.resolve();
  }

  const rawData = buildRequestData(envelope, url, params);
  // 无效值不发送（全局统一处理，默认开启）：''、'   '、null、undefined、NaN、
  // []、{}、'undefined'/'null'/'NaN' 这类空值字段直接不发（0 / false 保留）
  const data = shouldValidateValues({ validate, url })
    ? cleanRequestData(envelope, rawData)
    : rawData;
  const propertyName = WRITE_METHODS.includes(method.toLowerCase())
    ? 'data'
    : 'params';
  const headers = buildRequestHeaders({
    method,
    url,
    auth,
    token: params.token
  });

  return http({
    method,
    url,
    baseURL: getServiceBaseURL(service),
    headers,
    ...(timeout ? { timeout } : {}),
    [propertyName]: data
  });
}

// 特殊场景（如平铺 GET、文件上传）需要用到实例或 axios 本身时，统一从这里取
export { http };
export default httpRequest;
